/**
 * Translation Intent Detection Utilities
 * 
 * Detects when a user wants to translate a document (Azure AI) vs just translate text (LLM).
 */

// Keywords that indicate document translation intent
const TRANSLATION_KEYWORDS = {
    en: [
        'translate', 'translation', 'convert', 'localize',
        'translate this', 'translate the', 'translate to',
        'translate document', 'translate file', 'translate my'
    ],
    he: [
        'תרגם', 'תרגום', 'לתרגם', 'תתרגם',
        'תרגם את', 'תרגם לי', 'תרגם את המסמך', 'תרגם את הקובץ'
    ]
};

// Keywords that indicate document-specific context
const DOCUMENT_CONTEXT_KEYWORDS = {
    en: [
        'document', 'file', 'pdf', 'word', 'excel', 'powerpoint',
        'uploaded', 'attached', 'this file', 'the file', 'my file',
        'this document', 'the document', 'my document'
    ],
    he: [
        'מסמך', 'קובץ', 'הקובץ', 'המסמך',
        'הועלה', 'מצורף', 'הזה', 'שהעליתי'
    ]
};

// Keywords that indicate the user wants to translate AI response or text (NOT document)
const RESPONSE_TRANSLATION_KEYWORDS = {
    en: [
        'rewrite', 'write it in', 'say it in', 'tell me in',
        'the response', 'your response', 'this response', 'the answer',
        'your answer', 'this answer', 'the text', 'this text',
        'translate this to', 'translate it to', 'translate to',
        'in english', 'in hebrew', 'in arabic', 'in french', 'in spanish',
        'באנגלית', 'בעברית', 'בערבית'
    ],
    he: [
        'תכתוב ב', 'כתוב ב', 'תגיד ב', 'תאמר ב', 'אמור ב',
        'את התשובה', 'התשובה', 'את התגובה', 'התגובה',
        'את הטקסט', 'הטקסט הזה', 'תתרגם ל', 'תרגם ל',
        'באנגלית', 'בעברית', 'בערבית', 'בצרפתית', 'בספרדית'
    ]
};

// Language name to code mapping
const LANGUAGE_MAP: Record<string, string> = {
    // English names
    'hebrew': 'he',
    'english': 'en',
    'arabic': 'ar',
    'french': 'fr',
    'german': 'de',
    'spanish': 'es',
    'russian': 'ru',
    'chinese': 'zh-Hans',
    'japanese': 'ja',
    'portuguese': 'pt',
    'italian': 'it',
    'korean': 'ko',
    'polish': 'pl',
    'turkish': 'tr',
    'ukrainian': 'uk',
    
    // Hebrew names
    'עברית': 'he',
    'אנגלית': 'en',
    'ערבית': 'ar',
    'צרפתית': 'fr',
    'גרמנית': 'de',
    'ספרדית': 'es',
    'רוסית': 'ru',
    'סינית': 'zh-Hans',
    'יפנית': 'ja',
    'פורטוגזית': 'pt',
    'איטלקית': 'it',
    'קוריאנית': 'ko',
    'פולנית': 'pl',
    'טורקית': 'tr',
    'אוקראינית': 'uk',
};

/**
 * Detect if a message indicates document translation intent.
 * 
 * @param message - The user's message
 * @param hasUploadedFiles - Whether there are uploaded files in context
 * @returns true if document translation is intended, false for text translation (LLM)
 */
export function detectTranslationIntent(message: string, hasUploadedFiles: boolean): boolean {
    const lowerMessage = message.toLowerCase();
    
    // First, check if user wants to translate AI response (not document)
    // These patterns indicate text/response translation → LLM handles it
    const isResponseTranslation = 
        RESPONSE_TRANSLATION_KEYWORDS.en.some(kw => lowerMessage.includes(kw)) ||
        RESPONSE_TRANSLATION_KEYWORDS.he.some(kw => message.includes(kw));
    
    if (isResponseTranslation) {
        // Check if document context is also present - if so, document translation wins
        const hasDocumentContext = 
            DOCUMENT_CONTEXT_KEYWORDS.en.some(kw => lowerMessage.includes(kw)) ||
            DOCUMENT_CONTEXT_KEYWORDS.he.some(kw => message.includes(kw));
        
        if (!hasDocumentContext) {
            // User wants to translate response/text, not document → LLM handles it
            return false;
        }
    }
    
    // Check for translation keywords
    const hasTranslationKeyword = 
        TRANSLATION_KEYWORDS.en.some(kw => lowerMessage.includes(kw)) ||
        TRANSLATION_KEYWORDS.he.some(kw => message.includes(kw));
    
    if (!hasTranslationKeyword) {
        return false;
    }
    
    // If no files uploaded, this is text translation → LLM handles it
    if (!hasUploadedFiles) {
        return false;
    }
    
    // Check for document-specific context
    const hasDocumentContext = 
        DOCUMENT_CONTEXT_KEYWORDS.en.some(kw => lowerMessage.includes(kw)) ||
        DOCUMENT_CONTEXT_KEYWORDS.he.some(kw => message.includes(kw));
    
    // Only trigger document translation if there's explicit document context
    // This prevents "translate this to Hebrew" from triggering doc translation
    // when the user just wants to translate the previous AI response
    return hasDocumentContext;
}

/**
 * Extract target language from user message.
 * 
 * @param message - The user's message
 * @returns Language code (e.g., 'he', 'en') or null if not detected
 */
export function extractTargetLanguage(message: string): string | null {
    const lowerMessage = message.toLowerCase();
    
    // Check for direct language mentions
    for (const [langName, langCode] of Object.entries(LANGUAGE_MAP)) {
        if (lowerMessage.includes(langName) || message.includes(langName)) {
            return langCode;
        }
    }
    
    // Check for "to X" patterns
    const toPatternMatch = lowerMessage.match(/(?:translate|convert|localize)\s+(?:to|into)\s+(\w+)/i);
    if (toPatternMatch) {
        const targetLang = toPatternMatch[1].toLowerCase();
        if (LANGUAGE_MAP[targetLang]) {
            return LANGUAGE_MAP[targetLang];
        }
    }
    
    // Check for Hebrew patterns "תרגם ל"
    const hebrewToMatch = message.match(/תרגם\s*(?:ל|אל)\s*(.+?)(?:\s|$)/);
    if (hebrewToMatch) {
        const targetLang = hebrewToMatch[1].trim();
        if (LANGUAGE_MAP[targetLang]) {
            return LANGUAGE_MAP[targetLang];
        }
    }
    
    return null;
}

/**
 * Determine if this is a "translate text" request that should go to LLM.
 * These are inline text translations, not document translations.
 * 
 * @param message - The user's message
 * @returns true if this is inline text translation (LLM), false otherwise
 */
export function isInlineTextTranslation(message: string): boolean {
    const lowerMessage = message.toLowerCase();
    
    // Patterns that indicate inline text translation:
    // - "translate 'hello' to Hebrew"
    // - "translate: some text here"
    // - "what does X mean in Hebrew"
    
    const inlinePatterns = [
        /translate\s*['":]/, // translate "text" or translate: text
        /translate\s+the\s+word/i,
        /translate\s+this\s+text/i,
        /translate\s+the\s+following/i,
        /what\s+does\s+.*\s+mean\s+in/i,
        /how\s+do\s+(?:you|I)\s+say/i,
    ];
    
    return inlinePatterns.some(pattern => pattern.test(lowerMessage));
}

/**
 * Detect if a message indicates PDF to Word conversion intent.
 * 
 * @param message - The user's message
 * @param hasUploadedFiles - Whether there are uploaded files in context
 * @param hasPdfFile - Whether any uploaded file is a PDF
 * @returns true if PDF to Word conversion is intended
 */
export function detectPdfToWordIntent(message: string, hasUploadedFiles: boolean, hasPdfFile: boolean = false): boolean {
    if (!hasUploadedFiles || !hasPdfFile) {
        return false;
    }
    
    const lowerMessage = message.toLowerCase();
    
    // Keywords that indicate PDF to Word conversion
    const conversionPatterns = [
        // English patterns
        /convert.*to\s*word/i,
        /convert.*to\s*docx/i,
        /convert.*pdf.*word/i,
        /pdf.*to\s*word/i,
        /make.*word/i,
        /change.*to\s*word/i,
        /turn.*into\s*word/i,
        /export.*word/i,
        /save.*as\s*word/i,
        /as\s+a?\s*word\s*(document|file)?/i,
        /to\s+a?\s*word\s*(document|file)?$/i,
        /word\s*format/i,
        /docx\s*format/i,
        // Hebrew patterns
        /המר.*לוורד/i,
        /להמיר.*לוורד/i,
        /תמיר.*לוורד/i,
        /הפוך.*לוורד/i,
        /שמור.*כוורד/i,
        /לפורמט\s*וורד/i,
    ];
    
    return conversionPatterns.some(pattern => pattern.test(lowerMessage));
}
